
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ServicesPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsHeaderVisible(false);
        setIsMobileMenuOpen(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 bg-white shadow-sm z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold text-black select-none cursor-pointer" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link>
              <Link to="/services" className="text-blue-400 cursor-pointer">Services</Link>
              <Link to="/solutions" className="hover:text-blue-400 transition-colors cursor-pointer">Solutions</Link>
              <Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About</Link>
              <Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button 
                className="text-gray-600 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 mt-4 py-4">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Home
                </Link>
                <Link 
                  to="/services" 
                  className="text-blue-400 px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Services
                </Link>
                <Link 
                  to="/solutions" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Solutions
                </Link>
                <Link 
                  to="/about" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/0520359edfda678a5b16143ada8325bb.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="block">Comprehensive</span>
            <span className="block text-blue-400">Automation Services</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-300">
            From consultation to implementation and ongoing support, we provide end-to-end warehouse automation services
          </p>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Core Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete warehouse automation services designed to transform your operations and maximize efficiency
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {[
              {
                icon: 'ri-search-line',
                title: 'Consultation & Assessment',
                description: 'Comprehensive analysis of your current operations, identifying automation opportunities and developing customized strategies for maximum ROI.',
                features: ['Operational Analysis', 'ROI Calculations', 'Technology Roadmap', 'Risk Assessment']
              },
              {
                icon: 'ri-tools-line',
                title: 'System Design & Engineering',
                description: 'Custom automation system design tailored to your specific requirements, ensuring seamless integration with existing infrastructure.',
                features: ['3D Modeling', 'Simulation Testing', 'Integration Planning', 'Performance Optimization']
              },
              {
                icon: 'ri-settings-3-line',
                title: 'Installation & Implementation',
                description: 'Professional installation and commissioning of automation systems with minimal disruption to your ongoing operations.',
                features: ['Project Management', 'Equipment Installation', 'System Testing', 'Staff Training']
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Support & Maintenance',
                description: 'Technical support and preventive maintenance programs to ensure optimal system performance and longevity.',
                features: ['24/7 Support', 'Preventive Maintenance', 'Remote Monitoring', 'Performance Analytics']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  <li className="text-gray-700 flex items-center">
                    <i className="ri-check-line text-blue-600 mr-2"></i>
                    Custom System Design
                  </li>
                  <li className="text-gray-700 flex items-center">
                    <i className="ri-check-line text-blue-600 mr-2"></i>
                    Professional Installation
                  </li>
                  <li className="text-gray-700 flex items-center">
                    <i className="ri-check-line text-blue-600 mr-2"></i>
                    Staff Training Programs
                  </li>
                  <li className="text-gray-700 flex items-center">
                    <i className="ri-check-line text-blue-600 mr-2"></i>
                    Ongoing Maintenance
                  </li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Service Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures successful automation implementation from start to finish
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'Initial consultation to understand your requirements, challenges, and goals'
              },
              {
                step: '02',
                title: 'Analysis',
                description: 'Detailed assessment of current operations and identification of automation opportunities'
              },
              {
                step: '03',
                title: 'Design',
                description: 'Custom system design with 3D modeling and simulation testing'
              },
              {
                step: '04',
                title: 'Implementation',
                description: 'Professional installation, testing, and staff training for seamless operation'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Services */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Specialized Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Advanced automation services for complex warehouse operations and specialized requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-robot-line',
                title: 'Robotic Integration',
                description: 'Advanced robotic systems for picking, packing, palletizing, and material handling operations'
              },
              {
                icon: 'ri-brain-line',
                title: 'AI & Machine Learning',
                description: 'Intelligent systems that learn and optimize warehouse operations through advanced algorithms'
              },
              {
                icon: 'ri-database-line',
                title: 'WMS Integration',
                description: 'Seamless integration with existing warehouse management systems and ERP platforms'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Safety Systems',
                description: 'Comprehensive safety protocols and systems to ensure secure automated operations'
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Performance Analytics',
                description: 'Real-time monitoring and analytics to track performance and identify optimization opportunities'
              },
              {
                icon: 'ri-graduation-cap-line',
                title: 'Training Programs',
                description: 'Comprehensive training programs for your team to maximize automation system benefits'
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-800 p-6 rounded-xl hover:bg-gray-700 transition-colors">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${service.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-blue-400">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8">
            Contact us today
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4 select-none" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Transforming warehouse operations with cutting-edge robotics, AI-powered systems, and intelligent automation solutions. We empower businesses to achieve unprecedented levels of efficiency, accuracy, and cost savings through innovative technology.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Novatronis Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
